import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { Job } from '../types';

/** Canonical jobs key — shared by the Scraper console history AND the dashboard widget. */
export const JOBS_KEY = ['jobs', 'list'] as const;

interface RawJob extends Partial<Job> {
  _id?: string;
}

/**
 * Normalize a job record. `Jobs.listForUser` returns raw docs (only `_id`),
 * while `Jobs.listAll` maps `id` — so we normalize defensively here.
 */
function normalizeJob(j: RawJob): Job {
  return {
    id: j.id ?? j._id ?? '',
    niche: j.niche ?? '',
    location: j.location ?? '',
    status: (j.status as Job['status']) ?? 'completed',
    leads_found: j.leads_found ?? 0,
    duplicates_skipped: j.duplicates_skipped ?? 0,
    error_message: j.error_message,
    started_at: j.started_at,
    finished_at: j.finished_at,
    owner_name: j.owner_name,
    owner_username: j.owner_username,
  };
}

/**
 * Single canonical jobs hook. The dashboard "Running Jobs" widget passes a
 * refetchInterval while mounted; the Scraper history reads the same cache.
 * One key → one cache → one logical request.
 */
export function useJobs(options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: JOBS_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ jobs: RawJob[] }>('/api/jobs');
      return (data.jobs ?? []).map(normalizeJob);
    },
    staleTime: 15_000,
    refetchInterval: options?.refetchInterval,
  });
}

export function useJobMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['jobs'] });

  const deleteJob = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/jobs/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  const stopJob = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ ok: boolean }>(`/api/jobs/${id}/stop`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { deleteJob, stopJob };
}
